/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Chats", description = "Use chats to ask questions about your data, analyze campaign performance, build audiences, and get recommendations, all in natural language.", lastUpdatedDate = new Date(1792454400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Chats",
        description: "Use chats to ask questions about your data, analyze campaign performance, build audiences, and get recommendations, all in natural language.",
        lastUpdatedDate: "Tue Oct 20 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Start a new chat",
          "depth": 0,
          "id": "start-a-new-chat"
        }, {
          "value": "Ask questions",
          "depth": 0,
          "id": "ask-questions"
        }, {
          "value": "Tips for better results",
          "depth": 1,
          "id": "tips-for-better-results"
        }, {
          "value": "Iterate and follow up",
          "depth": 0,
          "id": "iterate-and-follow-up"
        }, {
          "value": "Check what the agent used",
          "depth": 0,
          "id": "check-what-the-agent-used"
        }, {
          "value": "Go from insight to action",
          "depth": 0,
          "id": "go-from-insight-to-action"
        }, {
          "value": "Upload files",
          "depth": 0,
          "id": "upload-files"
        }, {
          "value": "Use suggested prompts",
          "depth": 0,
          "id": "use-suggested-prompts"
        }, {
          "value": "Share and manage chats",
          "depth": 0,
          "id": "share-and-manage-chats"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      p: "p",
      em: "em",
      hr: "hr",
      h2: "h2",
      img: "img",
      ul: "ul",
      li: "li",
      a: "a",
      h3: "h3"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Marketers, data teams"
            })]
          })
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Chats are the primary interface for interacting with Agents. Ask questions in natural language and get answers grounded in your warehouse data and connected marketing channels."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Chats let you have a conversation with an Agent about your customer data and marketing performance. You type a question, and the agent queries your data warehouse, analyzes the results, and returns answers with charts, tables, and recommendations."
      }), "\n", _jsx(_components.p, {
        children: "Conversations are persistent. You can follow up, refine your question, and dig deeper without starting over. Each chat maintains full context from earlier messages, so you can iterate naturally."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "agents/chats/audience-insights.png",
          alt: "Chat with audience segmentation insights"
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "start-a-new-chat",
        children: "Start a new chat"
      }), "\n", _jsxs(_components.p, {
        children: ["To start a chat, go to the ", _jsx(_components.strong, {
          children: "Agents"
        }), " tab and click ", _jsx(_components.strong, {
          children: "Chats"
        }), " in the left navigation. Click ", _jsx(_components.strong, {
          children: "New chat"
        }), " or type directly in the input field at the bottom of the screen."]
      }), "\n", _jsx(_components.p, {
        children: "Before you ask your first question, you can configure two things:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Parent model"
          }), " — select the parent model the agent should query from the dropdown in the bottom-right corner of the chat input. This determines which customer data the agent has access to."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Custom agent"
          }), " — optionally select a ", _jsx(_components.a, {
            href: "/agents/custom-agents",
            children: "custom agent"
          }), " to scope the conversation to a specific task or set of instructions. You can also open a custom agent and click ", _jsx(_components.strong, {
            children: "Use in chat"
          }), " to start a new conversation with it applied. If you don't select one, the default agent is used."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "agents/chats/select-parent-model.png",
          alt: "Select a parent model from the chat input"
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "agents/custom-agents/search-custom-agents.png",
          alt: "Select a custom agent"
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "ask-questions",
        children: "Ask questions"
      }), "\n", _jsx(_components.p, {
        children: "Type your question in natural language. The agent uses the selected parent model and available schema to query the right data and return a response."
      }), "\n", _jsx(_components.p, {
        children: "Questions can range from simple data pulls to complex multi-step analyses:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Quick counts"
          }), " — \"How many customers made a purchase in the last 30 days?\""]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Performance analysis"
          }), " — \"How are my Meta ads performing this month? Break down by campaign objective with spend, CPA, CTR, and ROAS.\""]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Audience research"
          }), " — \"Break down my customer base by segment. Show customer count, average order value, purchase frequency, and email opt-in rate per segment.\""]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Comparisons"
          }), " — \"Compare new and returning customers over the last 90 days — revenue contribution, order frequency, AOV, and channel mix.\""]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Campaign planning"
          }), " — \"I want to run an A/B test on our win-back campaign — 10% discount vs. free shipping. How should I set up the holdout and how long should I run it?\""]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Anomaly detection"
          }), " — \"Are there any unusual patterns in my campaign performance over the last 7 days compared to the prior 4-week average?\""]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "tips-for-better-results",
        children: "Tips for better results"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Be specific about metrics"
          }), " — \"Show CPA and ROAS by campaign\" produces better results than \"how are things going.\""]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Include time ranges"
          }), " — \"last 30 days\" or \"Q4 vs. Q3\" helps the agent scope results accurately."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Name your segments"
          }), " — \"Gold loyalty members\" or \"first-time buyers in the last 14 days\" gives the agent clear targeting context."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Give context on your goal"
          }), " — \"I'm trying to reduce CPA\" helps surface the right insights."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Ask for visualizations"
          }), " — add \"show as a bar chart\" or \"trend over time\" to any prompt. You can also provide a video or image and ask the agent to remix it into a GIF."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Ask for comparisons"
          }), " — \"Compare X vs. Y\" consistently drives richer analysis."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Iterate on the fly"
          }), " — \"Relax the purchase frequency to 2+. What's the size difference?\" lets you explore without starting a new conversation."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsxs(_components.strong, {
            children: ["Approve ", _jsx(_components.a, {
              href: "/context-hub/business-context#review-and-approve-definitions",
              children: "definitions"
            })]
          }), " — review the assumptions agents make and approve the correct ones so future conversations stay consistent."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Start in chat, then formalize"
          }), " — use chat to develop and test your analysis before saving it as a custom agent or report."]
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "iterate-and-follow-up",
        children: "Iterate and follow up"
      }), "\n", _jsx(_components.p, {
        children: "You don't need to get your question perfect on the first try. Follow up within the same chat to refine results:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "\"Break this down by channel.\""
        }), "\n", _jsx(_components.li, {
          children: "\"Now show the same data as a trend over time.\""
        }), "\n", _jsx(_components.li, {
          children: "\"Relax the purchase frequency to 2+. What's the size difference?\""
        }), "\n", _jsx(_components.li, {
          children: "\"Exclude customers who received an offer in the last 14 days.\""
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "The agent retains context from the full conversation, so each follow-up builds on what came before."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "check-what-the-agent-used",
        children: "Check what the agent used"
      }), "\n", _jsxs(_components.p, {
        children: ["Check what an answer is based on before you act on it. Select ", _jsx(_components.strong, {
          children: "Show context"
        }), " in the chat header to open the ", _jsx(_components.strong, {
          children: "Session context"
        }), " panel. It lists what the agent used in the chat:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Outputs"
          }), " — CSV and PowerPoint files the agent created."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Data"
          }), " — The parent model, and the audiences, traits, and journeys the agent created or referenced."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Context"
          }), " — The Context Hub files it read."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Definitions"
          }), " — The business definitions it applied."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "agents/chats/session-context-panel.png",
          alt: "Session context panel listing the data, Context Hub files, and Assumed and Verified definitions used in a chat"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["An answer that used definitions also shows pills counting them by status. A pill beside a paragraph or list item covers the definitions behind that part of the answer, and the pill at the end of the answer covers all of them. Select a pill to see its definitions. If a definition is wrong, select ", _jsx(_components.strong, {
          children: "View in Context Hub"
        }), " to correct or approve it. See ", _jsx(_components.a, {
          href: "/context-hub/business-context#review-and-approve-definitions",
          children: "Review and approve definitions"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "agents/chats/answer-definitions-pill.png",
          alt: "Pill at the end of an answer, hovered to list three Assumed definitions and one Verified definition"
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "go-from-insight-to-action",
        children: "Go from insight to action"
      }), "\n", _jsx(_components.p, {
        children: "After any analysis, you can ask the agent to take action on the results:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Build an audience"
          }), " — \"Now build an audience from this group.\" The agent creates a draft audience you can review and save to Customer Studio for activation."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Create a report"
          }), " — \"Convert this into a weekly report.\" The agent turns your analysis into a reusable report prompt you can schedule and deliver via email or Slack. See ", _jsx(_components.a, {
            href: "/agents/reports",
            children: "Reports"
          }), " for more detail."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Save a custom agent prompt"
          }), " — if you've refined a useful line of questioning, you can save the prompt as a custom agent for reuse. See ", _jsx(_components.a, {
            href: "/agents/custom-agents",
            children: "Custom agents"
          }), " for more detail."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["You can also access these actions from the ", _jsx(_components.strong, {
          children: "..."
        }), " menu in the top-right corner of any chat."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "agents/chats/create-from-chat.png",
          alt: "Create from chat menu"
        })
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Customer Studio also includes an ", _jsx(_components.a, {
            href: "/customer-studio/agents",
            children: "Agent in the audience builder"
          }), " for creating and refining audiences using natural language. Use Agents when you're starting from a question; use the Agent in the audience builder when you're already working on an audience definition."]
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "upload-files",
        children: "Upload files"
      }), "\n", _jsx(_components.p, {
        children: "You can upload files directly in the chat to give the agent additional context. Click the image icon in the chat input to attach a file."
      }), "\n", _jsx(_components.p, {
        children: "Common uses for file uploads include:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Report QA"
          }), " — upload an example report and ask the agent to replicate it using your warehouse data, then compare the results."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Metric definitions"
          }), " — upload a document with your organization's metric definitions so the agent can reference them."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Planning documents"
          }), " — upload campaign briefs, initiative overviews, or other planning materials the agent can reference when answering questions."]
        }), "\n"]
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["If metric definitions are useful across multiple chats or reports, add them as ", _jsx(_components.a, {
            href: "/context-hub/business-context#add-business-context",
            children: "files in Context Hub"
          }), " so every conversation references them automatically."]
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "agents/chats/upload-files.png",
          alt: "Upload files"
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "use-suggested-prompts",
        children: "Use suggested prompts"
      }), "\n", _jsx(_components.p, {
        children: "When you open a new chat, the agent suggests questions based on your data and connected channels. These suggestions appear as cards below the welcome message and are a good starting point if you're not sure what to ask."
      }), "\n", _jsx(_components.p, {
        children: "You can also ask the agent for suggestions directly:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "\"What are the top 5 things I should look at this week for my paid media campaigns?\""
        }), "\n", _jsx(_components.li, {
          children: "\"What questions should I be asking about my email performance?\""
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "agents/chats/suggested-prompts.png",
          alt: "Suggested prompts"
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "share-and-manage-chats",
        children: "Share and manage chats"
      }), "\n", _jsxs(_components.p, {
        children: ["Chats are ", _jsx(_components.strong, {
          children: "private by default"
        }), ". Only you can see your conversations. To share a chat with a colleague, click ", _jsx(_components.strong, {
          children: "Share"
        }), " in the top-right corner of the chat. From the dropdown, you can ", _jsx(_components.strong, {
          children: "Copy link"
        }), " to generate a shareable URL or ", _jsx(_components.strong, {
          children: "Share to Slack"
        }), " to post the chat directly to a Slack channel."]
      }), "\n", _jsxs(_components.p, {
        children: ["You can search past chats using the ", _jsx(_components.strong, {
          children: "Search chats"
        }), " field in the left sidebar. All conversations are preserved, so you can return to a previous analysis at any time."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "agents/chats/share-chat.png",
          alt: "Share chat"
        })
      })]
    });
  }
}
export default MDXContent;
